import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Divider from '@mui/material/Divider'
import MenuItem from '@mui/material/MenuItem'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import SearchIcon from '@mui/icons-material/Search'
import Add from '@mui/icons-material/Add'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import Title from '../../component/Title'
import ResetFilterButton from '../../component/ResetFilterButton'
import { LIST_FILTER_SEARCH_FIELD_SX, LIST_FILTER_SEARCH_BUTTON_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import ListTableToolbar, { LIST_TABLE_TOOLBAR_BUTTON_SX } from '../../component/ListTableToolbar'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import EditIcon from '../../component/EditIcon'
import DeleteAlertDialog from '../../component/DeleteAlertDialog'
import ListActionGroup from '../../component/ListActionGroup'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import { buildListSearch as buildListSearchQuery, createInitialListState, createListLinkTarget } from '../../utils/listPageState'

const DEFAULT_SEARCH_SELECT = {
    searchCategory: '',
}

const SEARCH_SELECT_PARAMS = {
    searchCategory: {
        param: 'category',
        defaultValue: DEFAULT_SEARCH_SELECT.searchCategory,
    },
}

const FAQ_CATEGORY_LABELS = {
    1: '상품관련',
    2: '주문/결제',
    3: '배송',
    4: '변경/취소/반품',
    5: '회원정보',
    6: '서비스이용',
}

const FAQ_TABLE_COLUMNS = [
    { width: 70 },
    { width: 150 },
    { width: 90 },
    { width: 110 },
    { width: 360 },
    { width: 180 },
    { width: 180 },
    { width: 120 },
]

const FAQ_TABLE_MIN_WIDTH = FAQ_TABLE_COLUMNS.reduce((sum, column) => sum + column.width, 0)

const FAQ_TEXT_CELL_SX = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}

const FAQ_LABEL_CELL_SX = {
    whiteSpace: 'nowrap',
}

const getFaqCategoryLabel = (category) => FAQ_CATEGORY_LABELS[Number(category)] || '서비스이용'

export default function Faqs() {
    const location = useLocation()
    const history = useHistory()
    const deleteInFlightRef = useRef(new Set())
    const silentListRefreshRef = useRef(false)
    const initialListStateRef = useRef(createInitialListState({
        location,
        defaultSearchSelect: DEFAULT_SEARCH_SELECT,
        selectParams: SEARCH_SELECT_PARAMS,
    }))
    const initialListState = initialListStateRef.current
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(null)
    const [totalCount, setTotalCount] = useState(0)
    const [data, setData] = useState([])
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 10
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [searchText, setSearchText] = useState(initialListState.searchText)
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery)
    const [searchSelect, setSearchSelect] = useState(initialListState.searchSelect)
    const [listErrorMessage, setListErrorMessage] = useState('')

    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/faq/getFaqList', { page: curPage, offset: offset, searchText: searchQuery, searchSelect: searchSelect })

            if (data.code === 1) {
                setTotalCount(data.data.count || 0)
                setData(data.data.rows || [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || 'FAQ 목록을 불러오지 못했습니다.'
                setTotalCount(0)
                setData([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = 'FAQ 목록을 불러오지 못했습니다.'
            setTotalCount(0)
            setData([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [curPage, history, searchQuery, searchSelect])

    useEffect(() => {
        const showSpinner = !silentListRefreshRef.current
        silentListRefreshRef.current = false
        getData(showSpinner)
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearchQuery({
            curPage,
            searchQuery,
            searchSelect,
            selectParams: SEARCH_SELECT_PARAMS,
        })

        if (location.search !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            })
        }
    }, [curPage, history, location.pathname, location.search, searchQuery, searchSelect])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const isBusy = Boolean(actionLoading)

    const hasActiveFilters = Boolean((searchQuery || '').trim())
        || String(searchSelect.searchCategory) !== String(DEFAULT_SEARCH_SELECT.searchCategory)

    // 조회 실패와 필터 결과 없음을 같은 테이블 영역에서 구분한다.
    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="FAQ 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button
                            type="button"
                            size="small"
                            variant="outlined"
                            onClick={() => getData(false)}
                            disabled={loading || isBusy}
                        >
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        if (hasActiveFilters) {
            return (
                <ListEmptyState
                    title="조건에 맞는 FAQ가 없습니다."
                    description="내용 검색어 또는 카테고리 필터를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilters} disabled={isBusy} />}
                />
            )
        }

        return (
            <ListEmptyState
                title="등록된 FAQ가 없습니다."
                description="FAQ를 등록하면 이 목록에 표시됩니다."
            />
        )
    }

    const updateSearchSelect = (e) => {
        if (isBusy) {
            return
        }

        if (curPage === 1) {
            silentListRefreshRef.current = true
        }

        setSearchSelect({
            ...searchSelect,
            [e.target.name]: e.target.value
        })
        setSearchQuery((searchText || '').trim())
        setCurPage(1)
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    const resetFilters = () => {
        if (isBusy) {
            return
        }

        setCurPage(1)
        setSearchText('')
        setSearchQuery('')
        setSearchSelect({
            ...DEFAULT_SEARCH_SELECT,
        })
    }

    // 입력값과 실제 조회값을 분리해 Enter/검색 버튼 시점만 제어한다.
    const searchButtonClick = () => {
        if (isBusy) {
            return
        }

        const trimmedSearchText = (searchText || '').trim()

        if (curPage === 1 && searchQuery === trimmedSearchText) {
            getData(false)
            return
        }

        setSearchQuery(trimmedSearchText)

        if (curPage !== 1) {
            setCurPage(1)
            return
        }

        if (searchQuery !== trimmedSearchText) {
            return
        }

        getData(false)
    }

    const handleDelete = async (id) => {
        if (deleteInFlightRef.current.has(id)) {
            return
        }

        try {
            deleteInFlightRef.current.add(id)
            setActionLoading({ type: 'delete', id })

            const { data: response } = await API.post('/api/v1/admin/faq/delete', { faqId: id })

            if (response.code === 1) {
                setOpenSnackbar(true)
                setSeveritySnackbar('success')
                setMessageSnackbar('FAQ를 삭제했습니다.')

                if (curPage > 1 && data.length === 1) {
                    setCurPage((prev) => prev - 1)
                } else {
                    await getData(false)
                }
            } else if (response.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(response.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('FAQ를 삭제하지 못했습니다.')
        } finally {
            deleteInFlightRef.current.delete(id)
            setActionLoading(null)
        }
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Grid item xs={12}>
                    <Spiner/>
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Grid>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>FAQ 리스트</Title>
                    <ListFilterBar>
                        <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilters} disabled={isBusy} />}>
                            <InputBase
                                sx={LIST_FILTER_SEARCH_FIELD_SX}
                                placeholder="내용"
                                type="search"
                                name="searchText"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                disabled={isBusy}
                            />
                            <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={isBusy}><SearchIcon /></IconButton>
                            <Divider sx={{ height: 28, m: 0.5, marginRight: 2 }} orientation="vertical" />
                            <TextField
                                label="카테고리"
                                select
                                id="searchCategory"
                                name="searchCategory"
                                value={searchSelect.searchCategory}
                                onChange={updateSearchSelect}
                                size="small"
                                sx={{ minWidth: 150 }}
                                disabled={isBusy}
                            >
                                <MenuItem value=''>전체</MenuItem>
                                <MenuItem value='1'>상품관련</MenuItem>
                                <MenuItem value='2'>주문/결제</MenuItem>
                                <MenuItem value='3'>배송</MenuItem>
                                <MenuItem value='4'>변경/취소/반품</MenuItem>
                                <MenuItem value='5'>회원정보</MenuItem>
                                <MenuItem value='6'>서비스이용</MenuItem>
                            </TextField>
                        </ListFilterForm>

                    </ListFilterBar>
                    <ListTableToolbar
                        right={(
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<Add />}
                                size="small"
                                disabled={isBusy}
                                sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                                onClick={() => history.push(createListLinkTarget({
                                    pathname: '/new-faq',
                                    search: location.search,
                                    curPage: curPage,
                                    searchText: searchQuery,
                                    searchSelect: searchSelect,
                                }))}
                            >
                                등록
                            </Button>
                        )}
                    />

                    <ListTableContainer>
                        <Table size="small" sx={{ minWidth: FAQ_TABLE_MIN_WIDTH, tableLayout: 'fixed' }}>
                            <colgroup>
                                {FAQ_TABLE_COLUMNS.map((column, index) => (
                                    <col key={`faq-column-${index}`} style={{ width: column.width }} />
                                ))}
                            </colgroup>
                            <caption>Total Count: {totalCount}</caption>
                            <TableHead>
                                <TableRow>
                                    <TableCell>No</TableCell>
                                    <TableCell>카테고리</TableCell>
                                    <TableCell>순서</TableCell>
                                    <TableCell>노출여부</TableCell>
                                    <TableCell>제목</TableCell>
                                    <TableCell>등록일</TableCell>
                                    <TableCell>수정일</TableCell>
                                    <TableCell align="right">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.length > 0 ? (
                                    data.map((row) => (
                                        <TableRow key={row.faqId}>
                                            <TableCell>{row.rownum}</TableCell>
                                            <TableCell sx={FAQ_LABEL_CELL_SX}>{getFaqCategoryLabel(row.category)}</TableCell>
                                            <TableCell>{row.order}</TableCell>
                                            <TableCell sx={FAQ_LABEL_CELL_SX}>{row.status === 1 ? <VisibilityIcon/> : <VisibilityOffIcon/>}</TableCell>
                                            <TableCell className="listTableLimitText" sx={FAQ_TEXT_CELL_SX}>{row.title || '-'}</TableCell>
                                            <TableCell sx={FAQ_LABEL_CELL_SX}>{row.regTime || '-'}</TableCell>
                                            <TableCell sx={FAQ_LABEL_CELL_SX}>{row.updTime || '-'}</TableCell>
                                            <TableCell align="right">
                                                <ListActionGroup disabled={isBusy}>
                                                    <EditIcon path={'faq'} id={row.faqId} search={location.search} curPage={curPage} searchText={searchQuery} searchSelect={searchSelect}/>
                                                    <DeleteAlertDialog handleDelete={handleDelete} id={row.faqId}/>
                                                </ListActionGroup>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={8}>
                                            {renderEmptyState()}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </ListTableContainer>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
